// src/pages/Dashboard/InstituicaoDashboardPage.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import {
  TrendingDown as TrendingDownIcon,
  Payments as PaymentsIcon,
  AccessTime as AccessTimeIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import LinearProgress from '@mui/material/LinearProgress';

// Recharts imports
import {
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';

// Material-UI imports
import {
  Typography,
  Grid,
  Box,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Select,
  FormControl,
  InputLabel,
   Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  MenuItem,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Menu,
  Paper,
  ChildCareIcon,
  Chip,
  Stack,
  Container,
  TableContainer,
  Badge,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardHeader,
  CardContent
} from '@mui/material';

import { listarPerfis } from '../../services/perfilService';

// Material Icons imports
import {
  Edit as EditIcon,
  Slideshow as SlideshowIcon,
  Star as StarIcon,
  Menu as MenuIcon,
  LibraryBooks as LibraryBooksIcon,
  Storage as StorageIcon,
  FindInPage as FindInPageIcon,
  GavelRounded as GavelIcon,
  VpnKey as VpnKeyIcon,
  Link as LinkIcon,
  AccountCircle,
  Logout as LogoutIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Campaign as CampaignIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Class as ClassIcon,
  Subject as SubjectIcon,
  BarChart as BarChartIcon,
  Email as EmailIcon,
  Dashboard as DashboardIcon,
  Notifications as NotificationsIcon,
  School as SchoolIcon,
  CalendarToday as CalendarTodayIcon,
  Visibility as VisibilityIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { green, blue, orange, red, purple, grey } from '@mui/material/colors';

// >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
// 1) NOVOS IMPORTS (conforme solicitado):
import { Security as SecurityIcon } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import useLogAccess from '../../hooks/useLogAccess';
import InstituicaoLogsTable from '../../components/Logs/InstituicaoLogsTable';
import InstituicaoLogFilter from '../../components/Logs/InstituicaoLogFilter';
import { logService } from '../../services/logService';

import GerirPapeisUsuario from '../../components/Usuarios/GerirPapeisUsuario';

// <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

// Local components imports
import GraficoCampanhas from '../../components/Dashboard/GraficoCampanhas';
import SliderInstituicao from '../../components/Instituicoes/SliderInstituicao';
import DestaquesInstituicao from '../../components/Instituicoes/DestaquesInstituicao';
import NewsletterForm from '../../components/Instituicoes/NewsletterForm';
import EstudantesAtivosTable from '../../components/Estudantes/EstudantesAtivosTable';

// Services imports
import planosPagamentoService from '../../services/planosPagamentoService';
import { obterDadosDashboardInstituicao } from '../../services/dashboardService';
import { formatarData, formatarMoeda } from '../../utils/formatters';

// Hooks imports
import useAuth from '../../hooks/useAuth';
import useNotificacoes from '../../hooks/useNotificacoes';
import { useEstudantesAtivos } from '../../hooks/useEstudantesAtivos';

// Constants imports
import { FORMATOS_DATA } from '../../config/constants';

// Drawer width constant
const DRAWER_WIDTH = 280;

// Styles object
const styles = {
  appBar: {
    backgroundColor: 'white',
    color: 'text.primary',
    boxShadow: 1,
    zIndex: (theme) => theme.zIndex.drawer + 1,
  },
  mainContent: {
    flexGrow: 1,
    backgroundColor: grey[50],
    minHeight: '100vh',
    width: '100%',
    overflow: 'auto',
    paddingTop: '64px',
  },
  contentContainer: {
    width: '100%',
    maxWidth: '100%',
    margin: '0 auto',
    padding: { xs: 2, md: 3 },
  },
  fullWidthSection: {
    width: '100%',
    marginBottom: 4,
    '& > *': {
      width: '100%',
    },
  },
  widget: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 2,
    overflow: 'hidden',
    position: 'relative',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: 3,
    },
  },
  widgetHeader: {
    width: '100%',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: 1,
    borderColor: 'divider',
  },
  statCard: {
    width: '100%',
    height: '100%',
    borderRadius: 2,
    padding: 3,
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: 3,
    },
  },
  carouselCard: {
    height: '100%',
    padding: 2,
    borderRadius: 2,
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: 3,
    },
  },
  drawer: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
      width: DRAWER_WIDTH,
      boxSizing: 'border-box',
      border: 'none',
      boxShadow: 2,
    },
  },
  menuItem: {
    mx: 2,
    borderRadius: 1,
    mb: 0.5,
    '&:hover': {
      backgroundColor: (theme) => theme.palette.primary.main + '1A',
    },
  },
};

// Slider settings
const sliderSettings = {
  dots: true,
  infinite: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: true,
  autoplaySpeed: 4000,
  pauseOnHover: true,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

// Initial dashboard data state
const initialDashboardData = {
  classes: [],
  cursos: [],
  campanhasAtivas: 0,
  totalPreInscricoes: 0,
  estudantesAtivos: 0,
  cursosOferecidos: 0,
  preInscricoesPorStatus: {},
  campanhasRecentes: [],
  sliders: [],
  estatisticas: {
    pagamentos: {
      total: 0,
      pendentes: 0,
      totalRecebido: 0,
      ultimosPagamentos: []
    },
    prestacoes: {
      total: 0,
      pagas: 0,
      pendentes: 0
    },
    estudantes: [],
    estudantesEmDia: 0,
    estudantesEmAtraso: 0,
    taxaInadimplencia: 0,
    evolucaoPagamentos: [],
    _metadata: {
      timestamp: new Date().toISOString(),
      totalRegistros: 0
    }
  }
};

// Adicionar esta nova função após as importações
const getDashboardMenuItems = (perfilAtivo, tipoInstituicao, instituicaoId, user, modulosAtivos) => {


  
  const hasPermission = (permissions, requiresModule) => {
  // 1. Admin institucional sempre tem acesso
  if (user?.tipo === 'instituicao' && 
      (user?.perfilAdmin === 'ADMIN_INSTITUICAO' || 
       user?.perfilAdmin === 'AUDITOR_INTERNO')) {
    if (requiresModule) {
      return modulosAtivos?.includes(requiresModule) || false;
    }
    return true;
  }

  // 2. Se é instituição SEM perfilAdmin → acesso baseado em módulos
  if (user?.tipo === 'instituicao') {
    // Se requer módulo específico, verificar módulos ativos
    if (requiresModule) {
      return modulosAtivos?.includes(requiresModule) || false;
    }
    // Se não requer módulo E não tem permissões definidas → PERMITIR (acesso base)
    if (!permissions || permissions.length === 0) {
      return true;
    }
    // Se tem permissões E tem perfil ativo → verificar permissões
    if (perfilAtivo?.permissoes) {
      return permissions.some(perm => perfilAtivo.permissoes.includes(perm));
    }
    // Instituição sem perfil mas item tem permissões → NEGAR
    return false;
  }

  // 3. Outros tipos de usuário → verificar permissões
  if (perfilAtivo?.permissoes && permissions?.length) {
    return permissions.some(perm => perfilAtivo.permissoes.includes(perm));
  }

  return false;
};
  // O resto da função permanece igual
  const menuItems = [
    {
      category: 'Principal',
      items: [
        {
          text: 'Campanhas',
          icon: CampaignIcon,
          path: '/campanhas',
          badge: 3,
          requiresModule: 'INSCRICOES',
          permissions: ['gerenciar_campanhas']
        },
        {
          text: 'Pré-Inscrições',
          icon: AssignmentIcon,
          path: '/pre-inscricoes',
          badge: 12,
          requiresModule: 'INSCRICOES',
          permissions: ['gerenciar_preinscricoes']
        },
        {
          text: 'Planos de Pagamento',
          icon: PaymentIcon,
          path: '/planos-pagamento',
          badge: null,
          requiresModule: 'PAGAMENTOS',
          permissions: ['gerenciar_planos_pagamento', 'vincular_estudantes_planos']
        }
      ],
    },
    {
      category: 'Gestão Acadêmica',
      items: [
        { 
          text: 'Estudantes', 
          icon: PeopleIcon, 
          path: '/estudantes',
          permissions: ['gerenciar_preinscricoes']
        },
        { 
          text: 'Cursos', 
          icon: SubjectIcon, 
          path: '/cursos',
          permissions: ['gerenciar_cursos']
        },
        { 
          text: 'Classes', 
          icon: ClassIcon, 
          path: '/classes',
          permissions: ['gerenciar_classes']
        },
        {
          text: 'Perfis',
          icon: SupervisorAccountIcon,
          path: `/instituicoes/${instituicaoId}/perfis`,
          permissions: ['gerenciar_perfis']
        }
      ],
    },
    // ── TCC e Investigação ──────────────────────────────────────────
    {
      category: 'TCC & Plágio/IA',
      items: [
        {
          text: 'TCC & Plágio/IA',
          icon: LibraryBooksIcon,
          path: '/tcc/overview',
          requiresModule: 'TCC_MONOGRAFIAS',
          permissions: []
        },
      ],
    },
    // ── Repositório Digital ─────────────────────────────────────────
    {
      category: 'Repositório',
      items: [
        {
          text: 'Repositório Digital',
          icon: StorageIcon,
          path: '/repositorio',
          requiresModule: 'REPOSITORIO_DIGITAL',
          permissions: []
        },
      ],
    },
    // ── Módulos Educacionais ────────────────────────────────────────
    {
      category: 'Módulos Educacionais',
      items: [
        {
          text: 'Creche',
          icon: SchoolIcon,
          path: '/creche',
          requiresModule: 'CRECHE',
          permissions: []
        }
      ],
    },
    // ── Vínculos e Convites ─────────────────────────────────────────
    {
      category: 'Vínculos e Convites',
      items: [
        {
          text: 'Gerenciar Códigos',
          icon: VpnKeyIcon,
          path: `/instituicoes/${instituicaoId}/convites`,
          requiresModule: 'INSCRICOES',
          permissions: ['gerenciar_perfis', 'gerenciar_preinscricoes']
        },
        {
          text: 'Estudantes Vinculados',
          icon: LinkIcon,
          path: `/instituicoes/${instituicaoId}/estudantes-vinculados`,
          requiresModule: 'INSCRICOES',
          permissions: ['gerenciar_perfis', 'gerenciar_preinscricoes']
        }
      ],
    },
    // ── Relatórios e Análises ───────────────────────────────────────
    {
      category: 'Relatórios e Análises',
      items: [
        {
          text: 'Relatórios Gerais',
          icon: BarChartIcon,
          path: `/relatorios/instituicao/${instituicaoId}`,
          permissions: ['acessar_relatorios']
        },
        {
          text: 'Relatório Financeiro',
          icon: PaymentIcon,
          path: instituicaoId ? `/relatorios/financeiro/${instituicaoId}` : '/relatorios/financeiro',
          requiresModule: 'PAGAMENTOS',
          permissions: ['acessar_relatorios_financeiros']
        },
        {
          text: 'Análise de Inadimplência',
          icon: TrendingDownIcon,
          path: instituicaoId
            ? `/relatorios/financeiro/${instituicaoId}/inadimplencia`
            : '/relatorios/financeiro/inadimplencia',
          requiresModule: 'PAGAMENTOS',
          permissions: ['acessar_relatorios_financeiros']
        },
        {
          text: 'Métodos de Pagamento',
          icon: PaymentsIcon,
          path: instituicaoId
            ? `/relatorios/financeiro/${instituicaoId}/metodos-pagamento`
            : '/relatorios/financeiro/metodos-pagamento',
          requiresModule: 'PAGAMENTOS',
          permissions: ['acessar_relatorios_financeiros']
        }
      ],
    },
    // ── Configurações ───────────────────────────────────────────────
    {
      category: 'Configurações',
      items: [
        {
          text: 'Gerenciar Perfis',
          icon: SupervisorAccountIcon,
          path: `/instituicoes/${instituicaoId}/perfis`,
          permissions: ['gerenciar_perfis']
        },
        {
          text: 'Gerenciar Sliders',
          icon: SlideshowIcon,
          path: `/instituicoes/${instituicaoId}/gerenciar-slider`,
          permissions: ['gerenciar_conteudo']
        },
        {
          text: 'Gerenciar Destaques',
          icon: StarIcon,
          path: '/gerenciar-destaques',
          permissions: ['gerenciar_conteudo']
        },
        {
          text: 'Segurança',
          icon: SecurityIcon,
          path: `/instituicoes/${instituicaoId}/configuracoes/seguranca`,
          permissions: ['gerenciar_seguranca']
        }
      ],
    }
  ];

  // Filtra os itens baseado no tipo de instituição e permissões
  const filteredMenu = menuItems
    .map(category => {
      const filteredItems = category.items.filter(item => {
        if (item.text === 'Cursos' && tipoInstituicao !== 'Universidade') return false;
        if (item.text === 'Classes' && tipoInstituicao !== 'Colégio') return false;
        return hasPermission(item.permissions || [], item.requiresModule);
      });

      return { ...category, items: filteredItems };
    })
    .filter(category => category.items.length > 0);

  return filteredMenu;
};

// Auxiliary Components
const ModernAppHeader = React.memo(({ handleDrawerToggle, user, handleMenu }) => {
  return (
    <AppBar position="fixed" sx={styles.appBar}>
      <Toolbar>
        <IconButton
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 2, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        <DashboardIcon sx={{ mr: 2, color: blue[500] }} />

        <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
          Dashboard Institucional
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton>
            <Badge badgeContent={4} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          <IconButton>
            <Badge badgeContent={2} color="secondary">
              <EmailIcon />
            </Badge>
          </IconButton>

          <Chip
            icon={<AccountCircle />}
            label={user.nome}
            onClick={handleMenu}
            variant="outlined"
            sx={{
              borderRadius: '16px',
              '&:hover': { backgroundColor: grey[100] },
            }}
          />
        </Stack>
      </Toolbar>
    </AppBar>
  );
});

ModernAppHeader.propTypes = {
  handleDrawerToggle: PropTypes.func.isRequired,
  user: PropTypes.shape({
    nome: PropTypes.string.isRequired,
  }).isRequired,
  handleMenu: PropTypes.func.isRequired,
};

ModernAppHeader.displayName = 'ModernAppHeader';

const ModernStatCard = React.memo(({ title, value, description, icon: Icon, color, trend }) => {
  const formatValue = (val) => {
    if (val === undefined || val === null) return '0';
    if (typeof val === 'number') return val.toLocaleString();
    return val;
  };

  return (
    <Paper
      elevation={1}
      sx={{
        ...styles.statCard,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: color + '1A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ color, fontSize: 24 }} />
        </Box>
        {trend !== undefined && (
          <Chip
            label={`${trend > 0 ? '+' : ''}${trend}%`}
            color={trend > 0 ? 'success' : 'error'}
            size="small"
            variant="outlined"
            sx={{ height: 24 }}
          />
        )}
      </Box>

      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
          {formatValue(value)}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
          {title}
        </Typography>

        {description && (
          <Typography variant="caption" color="text.secondary">
            {description}
          </Typography>
        )}
      </Box>
    </Paper>
  );
});

ModernStatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  description: PropTypes.string,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
  trend: PropTypes.number,
};

ModernStatCard.defaultProps = {
  description: '',
  trend: undefined,
};

ModernStatCard.displayName = 'ModernStatCard';

const ModernWidget = React.memo(({ title, children, icon: Icon, color, action }) => {
  return (
    <Paper
      elevation={1}
      sx={{
        ...styles.widget,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          ...styles.widgetHeader,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: color + '1A',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ color, fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
        </Stack>
        {action}
      </Box>
      <Box
        sx={{
          p: 2,
          flexGrow: 1,
          overflow: 'auto',
          height: '100%',
          '& > *': {
            width: '100%',
          },
        }}
      >
        {children}
      </Box>
    </Paper>
  );
});

ModernWidget.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string.isRequired,
  action: PropTypes.node,
};

ModernWidget.displayName = 'ModernWidget';

const PagamentosPorEstudante = React.memo(({ estudantes, planoAtivo }) => {
  if (!Array.isArray(estudantes) || estudantes.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={6} align="center">
          <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" color="text.secondary">
              Nenhum estudante encontrado
            </Typography>
            {planoAtivo && (
              <Button
                size="small"
                variant="outlined"
                component={Link}
                to={`/planos-pagamento/${planoAtivo}/adicionar-estudante`}
              >
                Adicionar Estudante
              </Button>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  return estudantes.map((estudante) => (
    <TableRow key={estudante.id}>
      <TableCell>{estudante.nome}</TableCell>
      <TableCell align="right">
        {formatarMoeda(estudante.valorTotal)}
      </TableCell>
      <TableCell align="right">
        {formatarMoeda(estudante.totalPago)}
      </TableCell>
      <TableCell>
        <Chip
          label={estudante.status === 'EM_DIA' ? 'Em dia' : 'Em atraso'}
          color={estudante.status === 'EM_DIA' ? 'success' : 'warning'}
          size="small"
        />
      </TableCell>
      <TableCell>
        {estudante.ultimoPagamento
          ? formatarData(estudante.ultimoPagamento, FORMATOS_DATA.PADRAO)
          : 'Nenhum pagamento'
        }
      </TableCell>
      <TableCell align="center">
        <Button
          size="small"
          variant="outlined"
          component={Link}
          to={`/planos-pagamento/${planoAtivo}/estudante/${estudante.id}`}
          disabled={!planoAtivo}
        >
          Ver Detalhes
        </Button>
      </TableCell>
    </TableRow>
  ));
});

PagamentosPorEstudante.propTypes = {
  estudantes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nome: PropTypes.string,
    valorTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    totalPago: PropTypes.number,
    status: PropTypes.string,
    ultimoPagamento: PropTypes.string
  })),
  planoAtivo: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

PagamentosPorEstudante.defaultProps = {
  estudantes: [],
  planoAtivo: null
};

PagamentosPorEstudante.displayName = 'PagamentosPorEstudante';

const InstituicaoDashboardPage = () => {
  // 2) HOOKS DE AUTH E NAVEGAÇÃO (antes de usar user)
  const { user, logout } = useAuth();

  const fetchModulosAtivos = useCallback(async () => {
    if (!user?.instituicaoId) return;
    try {
      const response = await api.get(`/instituicoes/${user.instituicaoId}/modulos-ativos`);
      setModulosAtivos(response.data.modulos || []);
      if (response.data.trial) {
        setTrialInfo(response.data.trial);
      }
      setTrialCarregado(true);
    } catch (error) {
      console.error('Erro ao buscar módulos:', error);
      setTrialCarregado(true);
    }
  }, [user?.instituicaoId]);

  const navigate = useNavigate();
  const notificacaoContext = useNotificacoes();


  
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.tipo === 'instituicao' && !user.instituicaoId) {
      console.warn('Usuário de instituição sem instituicaoId');
    }
  }, [user, navigate]);

  // 3) DEMAIS ESTADOS EXISTENTES
  const [dashboardData, setDashboardData] = useState(initialDashboardData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState('');
  const [itemType, setItemType] = useState('classe');
  const [perfilCompleto, setPerfilCompleto] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [perfis, setPerfis] = useState([]);
  const [loadingPerfis, setLoadingPerfis] = useState(false);

const [currentPage, setCurrentPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);

const [paginationInfo, setPaginationInfo] = useState(null);



  const [openGerirPapeis, setOpenGerirPapeis] = useState(false);
const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
const [modulosAtivos, setModulosAtivos] = useState([]);
const [trialInfo, setTrialInfo] = useState({ activo: false, trial_fim: null, dias_restantes: 0, expirado: false });
const [trialCarregado, setTrialCarregado] = useState(false);

  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  // 4) NOVOS ESTADOS PARA LOGS (conforme solicitado):
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logError, setLogError] = useState(null);
  const [logFilters, setLogFilters] = useState({
    acao: '',
    entidade: '',
    status: '',
    dataInicio: null,
    dataFim: null
  });
  // <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Hook para estudantes
  const {
    estudantes,
    total: totalEstudantes,
    tipoInstituicao,
    loading: loadingEstudantes,
    error: errorEstudantes
  } = useEstudantesAtivos(user?.instituicaoId);

  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  // 5) HOOK useLogAccess (conforme solicitado):
  const {
    canAccessInstitutionLogs,
    permittedFilters,
    institutionId
  } = useLogAccess();
  // <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // Computed values
  const openMenu = Boolean(anchorEl);

  const chartData = useMemo(() => [
    {
      name: 'Em Dia',
      value: dashboardData?.estatisticas?.estudantesEmDia || 0
    },
    {
      name: 'Em Atraso',
      value: dashboardData?.estatisticas?.estudantesEmAtraso || 0
    }
  ], [dashboardData?.estatisticas?.estudantesEmDia, dashboardData?.estatisticas?.estudantesEmAtraso]);

  // ------------------------------------------------------------------
  const filteredMenuItems = useMemo(() =>
    getDashboardMenuItems(user?.perfilAtivo, tipoInstituicao, user?.instituicaoId, user, modulosAtivos),
    [user?.perfilAtivo, tipoInstituicao, user?.instituicaoId, user, modulosAtivos]
  );
  // ------------------------------------------------------------------

  // 6) FUNÇÕES DE EVENTOS
  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const handleMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleEditProfile = useCallback(() => {
    handleCloseMenu();
    navigate('/editar-perfil');
  }, [handleCloseMenu, navigate]);

  const handleLogout = useCallback(() => {
    handleCloseMenu();
    logout();
    navigate('/login');
  }, [handleCloseMenu, logout, navigate]);

  const handleItemChange = useCallback((event) => {
    setSelectedItem(event.target.value);
  }, []);

  const handleTypeChange = useCallback((event) => {
    setItemType(event.target.value);
    setSelectedItem('');
  }, []);

  const handleVerPreInscricoes = useCallback(() => {
    if (selectedItem) {
      navigate(`/pre-inscricoes/por-classe-curso/${selectedItem}?tipo=${itemType}`);
    }
  }, [selectedItem, itemType, navigate]);

  const handleCloseSnackbar = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  }, []);
// === Gestão do modal Gerir Papéis ===
const handleAbrirGerirPapeis = (usuario) => {
  setUsuarioSelecionado(usuario);
  setOpenGerirPapeis(true);
};

const handleFecharGerirPapeis = () => {
  setOpenGerirPapeis(false);
  setUsuarioSelecionado(null);
};

// Usaremos para recarregar a lista após salvar
const handleGerirPapeisSaved = async () => {
  await refreshPerfis(); // função criada no passo 4
  setOpenGerirPapeis(false);
  setUsuarioSelecionado(null);
};

  // 7) FUNÇÃO PARA VERIFICAR PERFIL
  const checkPerfilCompleto = useCallback((data) => {
    const isCompleto = !!(data.telefone && data.website && data.descricao);
    setPerfilCompleto(isCompleto);

    if (isCompleto && localStorage.getItem('perfilAtualizado') === 'true') {
      setSnackbarMessage('Seus dados estão atualizados');
      setOpenSnackbar(true);
      localStorage.removeItem('perfilAtualizado');
    }
  }, []);

  // 8) FUNÇÃO PARA BUSCAR DADOS DO DASHBOARD
  const fetchDashboardData = useCallback(async () => {
    if (!user?.instituicaoId) return;

    try {
      setLoading(true);
      const data = await obterDadosDashboardInstituicao(user.instituicaoId);

      // Buscar planos ativos — módulo pode não estar activo para novas instituições
      let planosAtivos = { data: [] };
      try {
        planosAtivos = await planosPagamentoService.listarPlanosPagamento({
          instituicaoId: user.instituicaoId,
          status: 'ATIVO'
        });
      } catch (planosErr) {
        if (planosErr?.response?.status !== 403) {
          console.error('Erro ao buscar planos:', planosErr);
        }
      }

      if (planosAtivos?.data?.length > 0) {
        try {
          // Buscar estatísticas de todos os planos
          const promisesEstatisticas = planosAtivos.data.map((plano) =>
            planosPagamentoService.obterEstatisticasPlano(plano.id)
          );

          const todasEstatisticas = await Promise.all(promisesEstatisticas);

          // Combinar estatísticas
          const estatisticasCombinadas = {
            prestacoes: { total: 0, pagas: 0, pendentes: 0 },
            pagamentos: { total: 0, pendentes: 0, totalRecebido: 0, ultimosPagamentos: [] },
            estudantes: [],
            estudantesEmDia: 0,
            estudantesEmAtraso: 0,
            evolucaoPagamentos: [],
            taxaInadimplencia: 0,
            _metadata: {
              timestamp: new Date().toISOString(),
              totalRegistros: 0
            }
          };

          todasEstatisticas.forEach((resposta) => {
            if (resposta?.data?.estatisticas) {
              const estatistica = resposta.data.estatisticas;

              estatisticasCombinadas.prestacoes.total += estatistica.prestacoes.total;
              estatisticasCombinadas.prestacoes.pagas += estatistica.prestacoes.pagas;
              estatisticasCombinadas.prestacoes.pendentes += estatistica.prestacoes.pendentes;

              estatisticasCombinadas.pagamentos.total += estatistica.pagamentos.total;
              estatisticasCombinadas.pagamentos.pendentes += estatistica.pagamentos.pendentes;
              estatisticasCombinadas.pagamentos.totalRecebido += estatistica.pagamentos.totalRecebido;

              if (estatistica.pagamentos.ultimosPagamentos?.length > 0) {
                estatisticasCombinadas.pagamentos.ultimosPagamentos = [
                  ...estatisticasCombinadas.pagamentos.ultimosPagamentos,
                  ...estatistica.pagamentos.ultimosPagamentos
                ]
                  .sort((a, b) => new Date(b.data) - new Date(a.data))
                  .slice(0, 5);
              }

              if (estatistica.estudantes?.length > 0) {
                estatisticasCombinadas.estudantes = [
                  ...estatisticasCombinadas.estudantes,
                  ...estatistica.estudantes
                ];
              }

              estatisticasCombinadas.estudantesEmDia +=
                estatistica.estudantes?.filter((e) => e.status === 'EM_DIA').length || 0;
              estatisticasCombinadas.estudantesEmAtraso +=
                estatistica.estudantes?.filter((e) => e.status === 'EM_ATRASO').length || 0;

              if (estatistica.evolucaoPagamentos?.length > 0) {
                estatisticasCombinadas.evolucaoPagamentos = [
                  ...estatisticasCombinadas.evolucaoPagamentos,
                  ...estatistica.evolucaoPagamentos
                ];
              }
            }
          });

          estatisticasCombinadas.taxaInadimplencia =
            estatisticasCombinadas.prestacoes.total > 0
              ? (estatisticasCombinadas.prestacoes.pendentes / estatisticasCombinadas.prestacoes.total) * 100
              : 0;

          estatisticasCombinadas._metadata = {
            timestamp: new Date().toISOString(),
            totalRegistros: estatisticasCombinadas.pagamentos.ultimosPagamentos.length,
            totalEstudantes: estatisticasCombinadas.estudantes.length
          };

          setDashboardData((prev) => ({
            ...prev,
            ...data,
            estatisticas: estatisticasCombinadas,
            planoAtivo: planosAtivos.data[0].id
          }));
        } catch (error) {
          console.error('Erro ao buscar estatísticas:', error);
          setDashboardData((prev) => ({
            ...prev,
            ...data
          }));
          if (notificacaoContext?.adicionarNotificacao) {
            notificacaoContext.adicionarNotificacao('Erro ao carregar estatísticas dos planos', 'error');
          }
        }
      } else {
        setDashboardData((prev) => ({
          ...prev,
          ...data
        }));
      }

      checkPerfilCompleto(data);
    } catch (error) {
      console.error('Erro ao buscar dados do dashboard:', error);
      if (error?.response?.status !== 403) {
        setError('Falha ao carregar os dados do dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [user?.instituicaoId, checkPerfilCompleto]);

  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  // 9) FUNÇÕES DE MANIPULAÇÃO DE LOGS (conforme solicitado):
  const fetchLogs = useCallback(async () => {
    if (!canAccessInstitutionLogs() || !user?.instituicaoId) return;

    try {
      setLoadingLogs(true);
      const response = await logService.listLogsInstituicao(user.instituicaoId, logFilters);

      if (response?.data?.success && Array.isArray(response.data.data)) {
        setLogs(response.data.data);
        setLogError(null);
      } else {
        console.error('Resposta inválida:', response);
        setLogError('Formato de resposta inválido');
        if (notificacaoContext?.adicionarNotificacao) {
          notificacaoContext.adicionarNotificacao(
            'Erro ao processar dados dos logs',
            'error'
          );
        }
      }
    } catch (error) {
      console.error('Erro ao buscar logs:', error);
      setLogError(error.response?.data?.message || 'Falha ao carregar logs da instituição');
      if (notificacaoContext?.adicionarNotificacao) {
        notificacaoContext.adicionarNotificacao(
          'Erro ao carregar logs: ' + (error.response?.data?.message || 'Erro desconhecido'),
          'error'
        );
      }
    } finally {
      setLoadingLogs(false);
    }
  }, [user?.instituicaoId, logFilters, canAccessInstitutionLogs]);

  const handleLogFilterChange = useCallback((field, value) => {
    if (field === 'reset') {
      setLogFilters({
        acao: '',
        entidade: '',
        status: '',
        dataInicio: null,
        dataFim: null
      });
    } else {
      setLogFilters((prev) => ({
        ...prev,
        [field]: value
      }));
    }
  }, []);
  // <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<

  // 10) USEEFFECT PARA BUSCAR DADOS DO DASHBOARD
  useEffect(() => {
    let mounted = true;

    if (mounted && user?.instituicaoId) {
      fetchDashboardData();
    }
    return () => {
      mounted = false;
    };
  }, [user?.instituicaoId, fetchDashboardData]);

  // >>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  // 11) USEEFFECT PARA BUSCAR LOGS (conforme solicitado):
  useEffect(() => {
    if (canAccessInstitutionLogs()) {
      fetchLogs();
    }
  }, [user?.instituicaoId, fetchLogs]);
  // <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<
const refreshPerfis = React.useCallback(async (page = 1) => {
  if (!user?.instituicaoId) return;
  setLoadingPerfis(true);
  try {
    const response = await listarPerfis(user.instituicaoId, page, 5);

    if (response && typeof response === 'object' && response.data && !Array.isArray(response)) {
      setPerfis(Array.isArray(response.data) ? response.data : []);
      setPaginationInfo(response.pagination);
      if (response.pagination) {
        setCurrentPage(response.pagination.currentPage);
        setTotalPages(response.pagination.totalPages);
      }
    } else if (Array.isArray(response)) {
      setPerfis(response);
      setPaginationInfo(null);
      setCurrentPage(1);
      setTotalPages(1);
    } else {
      console.error('Estrutura de resposta não reconhecida:', response);
      setPerfis([]);
      setPaginationInfo(null);
    }
    
  } catch (error) {
    console.error('❌ Erro ao carregar perfis:', error);
    if (notificacaoContext?.adicionarNotificacao) {
      notificacaoContext.adicionarNotificacao('Erro ao carregar perfis', 'error');
    }
  } finally {
    setLoadingPerfis(false);
  }
}, [user?.instituicaoId, notificacaoContext]);

useEffect(() => {
  if (user?.instituicaoId) {
    refreshPerfis(1);
  }
}, [user?.instituicaoId, refreshPerfis]);

useEffect(() => {
  if (user?.instituicaoId) {
    fetchModulosAtivos();
  }
}, [user?.instituicaoId, fetchModulosAtivos]);

// Re-fetch módulos quando o browser regressa ao foco (ex: após restart do servidor)
useEffect(() => {
  const onFocus = () => { if (user?.instituicaoId) fetchModulosAtivos(); };
  window.addEventListener('focus', onFocus);
  return () => window.removeEventListener('focus', onFocus);
}, [user?.instituicaoId, fetchModulosAtivos]);

// Funções de navegação
const handleNextPage = () => {
  if (currentPage < totalPages) {
    refreshPerfis(currentPage + 1);
  }
};

const handlePrevPage = () => {
  if (currentPage > 1) {
    refreshPerfis(currentPage - 1);
  }
};

const handlePageChange = (page) => {
  refreshPerfis(page);
};

  // Conteúdo do Drawer
  const drawer = useMemo(
    () => (
      <Box sx={{ overflow: 'auto', py: 2 }}>
        <Toolbar />
        {trialInfo.activo && !trialInfo.expirado && (
          <Box sx={{ mx: 2, mb: 2, p: 1.5, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200', borderRadius: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="warning.dark" display="block">
              Trial activo · {trialInfo.dias_restantes} dias
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Todos os módulos disponíveis para avaliação
            </Typography>
          </Box>
        )}
        {filteredMenuItems.map((category) => (
          <Box key={category.category} sx={{ mb: 3 }}>
            <Box sx={{ px: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                {category.category}
              </Typography>
              {trialInfo.activo && !trialInfo.expirado && ['TCC & Plágio/IA', 'Repositório', 'Módulos Educacionais'].includes(category.category) && (
                <Chip label="TRIAL" size="small" color="warning" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700 }} />
              )}
            </Box>
            <List>
              {category.items.map((item) => (
                <ListItem
                  button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  sx={styles.menuItem}
                >
                  <ListItemIcon>
                    <item.icon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                  {item.badge && (
                    <Chip
                      label={item.badge}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        ))}
      </Box>
    ),
    [filteredMenuItems, trialInfo]
  );


// Exibição principal
// Se não há usuário, não renderizar nada (será redirecionado)
if (!user) {
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

// Exibição principal
if (loading) {
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <CircularProgress />
    </Box>
  );
}
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <ModernAppHeader
        handleDrawerToggle={handleDrawerToggle}
        user={user}
        handleMenu={handleMenu}
      />

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{
          width: { sm: DRAWER_WIDTH },
          flexShrink: { sm: 0 },
        }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box component="main" sx={styles.mainContent}>
        <Container maxWidth={false} sx={styles.contentContainer}>
          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleEditProfile}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              Editar Perfil
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              Sair
            </MenuItem>
          </Menu>

          {/* Alerta de Perfil */}
          {!perfilCompleto && (
            <Alert
              severity="warning"
              sx={{ mb: 4 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  component={Link}
                  to="/completar-perfil/instituicao"
                >
                  Completar Perfil
                </Button>
              }
            >
              Por favor, complete seu perfil para ter acesso a todas as funcionalidades.
            </Alert>
          )}

          {/* Seção de Filtro para Pré-Inscrições — só se módulo INSCRICOES activo */}
          {modulosAtivos.includes('INSCRICOES') && <Box sx={{ mb: 4 }}>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="tipo-selecao-label">Tipo</InputLabel>
                    <Select
                      labelId="tipo-selecao-label"
                      id="tipo-selecao"
                      value={itemType}
                      label="Tipo"
                      onChange={handleTypeChange}
                    >
                      <MenuItem value="classe">Classe</MenuItem>
                      <MenuItem value="curso">Curso</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth disabled={!itemType}>
                    <InputLabel id="item-selecao-label">Selecione</InputLabel>
                    <Select
                      labelId="item-selecao-label"
                      id="item-selecao"
                      value={selectedItem}
                      label="Selecione"
                      onChange={handleItemChange}
                    >
                      {itemType === 'classe' &&
                        dashboardData.classes.map((classe) => (
                          <MenuItem key={classe.id} value={classe.id}>
                            {classe.nome}
                          </MenuItem>
                        ))}
                      {itemType === 'curso' &&
                        dashboardData.cursos.map((curso) => (
                          <MenuItem key={curso.id} value={curso.id}>
                            {curso.nome}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleVerPreInscricoes}
                    disabled={!selectedItem}
                    fullWidth
                  >
                    Ver Pré-Inscrições
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Box>}

          {/* Slider Principal */}
          <Box sx={styles.fullWidthSection}>
            <Box sx={{ width: '100%', borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
              <SliderInstituicao
                instituicaoId={user.instituicaoId}
                instituicaoNome={user.nome}
              />
            </Box>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SlideshowIcon />}
                component={Link}
                to={`/instituicoes/${user.instituicaoId}/gerenciar-slider`}
                sx={{ textTransform: 'none' }}
              >
                Administrar Slider
              </Button>
            </Box>
          </Box>

          {/* ── Banner de Trial ─────────────────────────────────────── */}
          {trialCarregado && trialInfo.activo && !trialInfo.expirado && (
            <Box sx={{ mb: 3 }}>
              <Paper elevation={0} sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: trialInfo.dias_restantes <= 3 ? 'error.light' : 'warning.light',
                borderRadius: 2,
                bgcolor: trialInfo.dias_restantes <= 3 ? 'error.50' : 'warning.50',
                background: trialInfo.dias_restantes <= 3
                  ? 'linear-gradient(135deg, #fff3f3 0%, #ffe0e0 100%)'
                  : 'linear-gradient(135deg, #fffbf0 0%, #fff3cd 100%)',
              }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <AccessTimeIcon sx={{ color: trialInfo.dias_restantes <= 3 ? 'error.main' : 'warning.main', fontSize: 28 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} color={trialInfo.dias_restantes <= 3 ? 'error.main' : 'warning.dark'}>
                        {trialInfo.dias_restantes <= 3
                          ? `Atenção: apenas ${trialInfo.dias_restantes} dia${trialInfo.dias_restantes !== 1 ? 's' : ''} de trial restante${trialInfo.dias_restantes !== 1 ? 's' : ''}!`
                          : `Período de trial activo — ${trialInfo.dias_restantes} dias restantes`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {trialInfo.trial_fim
                          ? `Expira em ${new Date(trialInfo.trial_fim).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}`
                          : 'Experimente todas as funcionalidades gratuitamente'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box sx={{ minWidth: 140 }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        {trialInfo.dias_restantes} / 14 dias
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.max(0, (trialInfo.dias_restantes / 14) * 100)}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'rgba(0,0,0,0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: trialInfo.dias_restantes <= 3 ? 'error.main' : 'warning.main'
                          }
                        }}
                      />
                    </Box>
                    <Chip
                      label="Subscrever agora"
                      color={trialInfo.dias_restantes <= 3 ? 'error' : 'warning'}
                      icon={<OpenInNewIcon />}
                      clickable
                      component={Link}
                      to="/modulos/subscrever"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          {trialCarregado && trialInfo.expirado && (
            <Alert severity="error" sx={{ mb: 3 }} action={
              <Button color="error" variant="contained" size="small" component={Link} to="/modulos/subscrever">
                Subscrever
              </Button>
            }>
              <strong>O período de trial terminou.</strong> O acesso às funcionalidades está suspenso até à subscrição de um plano.
            </Alert>
          )}

          {/* ── Onboarding (só para instituições novas) ─────────────── */}
          {(totalEstudantes === 0 || !perfilCompleto) && (
            <Box sx={{ mb: 3 }}>
              <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'primary.light', borderRadius: 2, bgcolor: '#f0f7ff' }}>
                <Typography variant="subtitle1" fontWeight={700} color="primary.main" gutterBottom>
                  🚀 Primeiros passos — configure a sua instituição
                </Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  {[
                    { label: 'Complete o perfil da instituição', done: perfilCompleto, link: `/instituicoes/${user.instituicaoId}/editar` },
                    { label: 'Adicione o primeiro estudante', done: totalEstudantes > 0, link: `/estudantes/${user.instituicaoId}/gerenciar` },
                    { label: 'Crie um plano de pagamento', done: !!dashboardData.planoAtivo, link: '/planos-pagamento/novo' },
                    { label: 'Configure perfis de acesso', done: perfis?.length > 1, link: `/instituicoes/${user.instituicaoId}/perfis` },
                  ].map((step, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box display="flex" alignItems="center" gap={1} sx={{
                        p: 1.5, borderRadius: 1.5,
                        bgcolor: step.done ? 'success.50' : 'white',
                        border: '1px solid',
                        borderColor: step.done ? 'success.200' : 'grey.200',
                      }}>
                        {step.done
                          ? <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          : <RadioButtonUncheckedIcon sx={{ color: 'grey.400', fontSize: 20 }} />}
                        <Typography variant="body2" sx={{ flexGrow: 1, color: step.done ? 'success.dark' : 'text.primary', textDecoration: step.done ? 'line-through' : 'none' }}>
                          {step.label}
                        </Typography>
                        {!step.done && (
                          <Button size="small" component={Link} to={step.link} sx={{ minWidth: 'auto', fontSize: '0.7rem' }}>
                            Ir
                          </Button>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          )}

          {/* Cards de Estatísticas */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Sempre visíveis */}
            <Grid item xs={12} sm={6} md={3}>
              <ModernStatCard
                title="Estudantes Ativos"
                value={dashboardData.estudantesAtivos}
                description="Estudantes atualmente ativos"
                icon={PeopleIcon}
                color={orange[600]}
                trend={15}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ModernStatCard
                title="Total de Estudantes"
                value={totalEstudantes || 0}
                description="Total de estudantes matriculados"
                icon={PeopleIcon}
                color={purple[600]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ModernStatCard
                title="Perfis Ativos"
                value={perfis?.length || 0}
                description="Perfis de usuários"
                icon={SupervisorAccountIcon}
                color={blue[600]}
                onClick={() => navigate(`/instituicoes/${user?.instituicaoId}/perfis`)}
              />
            </Grid>
            {/* Módulo INSCRICOES */}
            {modulosAtivos.includes('INSCRICOES') && (
              <Grid item xs={12} sm={6} md={3}>
                <ModernStatCard
                  title="Campanhas Ativas"
                  value={dashboardData.campanhasAtivas}
                  description="Campanhas em andamento"
                  icon={CampaignIcon}
                  color={green[600]}
                  trend={10}
                />
              </Grid>
            )}
            {modulosAtivos.includes('INSCRICOES') && (
              <Grid item xs={12} sm={6} md={3}>
                <ModernStatCard
                  title="Total de Pré-Inscrições"
                  value={dashboardData.totalPreInscricoes}
                  description="Pré-inscrições recebidas"
                  icon={AssignmentIcon}
                  color={blue[600]}
                  trend={-5}
                />
              </Grid>
            )}
            {/* Módulo CRECHE */}
            {modulosAtivos.includes('CRECHE') && (
              <Grid item xs={12} sm={6} md={3}>
                <ModernStatCard
                  title="Módulo Creche"
                  value="Activo"
                  description="Gestão da creche"
                  icon={SchoolIcon}
                  color={blue[600]}
                  onClick={() => navigate('/creche')}
                />
              </Grid>
            )}
            {/* Cursos (só se universidade) */}
            {tipoInstituicao === 'Universidade' && (
              <Grid item xs={12} sm={6} md={3}>
                <ModernStatCard
                  title="Cursos Oferecidos"
                  value={dashboardData.cursosOferecidos}
                  description="Cursos disponíveis"
                  icon={SubjectIcon}
                  color={purple[600]}
                  trend={-2}
                />
              </Grid>
            )}
          </Grid>
          {/* Tabela de Estudantes Ativos */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <ModernWidget
                title="Estudantes Ativos"
                icon={PeopleIcon}
                color={blue[600]}
                action={
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    component={Link}
                    to={`/estudantes/${user.instituicaoId}/gerenciar`}
                    sx={{ textTransform: 'none' }}
                  >
                  Administrar
                  </Button>
                }
              >
                <EstudantesAtivosTable
                  estudantes={estudantes}
                  loading={loadingEstudantes}
                  error={errorEstudantes}
                  tipoInstituicao={tipoInstituicao}
                />
              </ModernWidget>
            </Grid>
          </Grid>

          {/* Gráfico de Campanhas — só com módulo INSCRICOES */}
          {modulosAtivos.includes('INSCRICOES') && <Box sx={styles.fullWidthSection}>
            <ModernWidget
              title="Análise de Campanhas"
              icon={BarChartIcon}
              color={green[600]}
              action={
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  component={Link}
                  to={`/campanhas/${user.instituicaoId}/analise`}
                  sx={{ textTransform: 'none' }}
                >
               Administrar
                </Button>
              }
            >
              <GraficoCampanhas campanhas={dashboardData.campanhasAtivas} />
            </ModernWidget>
          </Box>}

          {/* Destaques */}
          <Box sx={styles.fullWidthSection}>
            <ModernWidget
              title="Destaques da Instituição"
              icon={StarIcon}
              color={orange[600]}
              action={
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  component={Link}
                  to={`/instituicoes/${user.instituicaoId}/gerenciar-destaques`}
                  sx={{ textTransform: 'none' }}
                >
                Administrar
                </Button>
              }
            >
              <Box sx={{ width: '100%', py: 1 }}>
                <DestaquesInstituicao instituicaoId={user.instituicaoId} />
              </Box>
            </ModernWidget>
          </Box>

          {modulosAtivos.includes('INSCRICOES') && <Box sx={styles.fullWidthSection}>
            <ModernWidget
              title="Campanhas Recentes"
              icon={CampaignIcon}
              color={green[600]}
              action={
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CampaignIcon />}
                  component={Link}
                  to="/campanhas"
                  sx={{ textTransform: 'none', minWidth: 'auto' }}
                >
                  Ver Todas
                </Button>
              }
            >
              <Box sx={{ width: '100%', py: 1, overflow: 'hidden' }}>
                {dashboardData.campanhasRecentes?.length > 0 ? (
                  <Slider {...sliderSettings}>
                    {dashboardData.campanhasRecentes.map((campanha) => (
                      <Box key={campanha.id} sx={{ px: 1 }}>
                        <Paper
                          sx={{
                            ...styles.carouselCard,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'transform 0.2s ease-in-out',
                            transform: 'translateZ(0)',
                            willChange: 'transform',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 3,
                            }
                          }}
                        >
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {campanha.titulo}
                          </Typography>
                          <Box sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                            minHeight: '24px'
                          }}>
                            <Box component="span" sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mr: 2,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              <SchoolIcon sx={{ fontSize: 18, mr: 0.5, flexShrink: 0 }} />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {campanha.instituicao || 'Instituição não especificada'}
                              </span>
                            </Box>
                          </Box>
                          <Box sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                            minHeight: '24px'
                          }}>
                            <Box component="span" sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mr: 2,
                              whiteSpace: 'nowrap'
                            }}>
                              <CalendarTodayIcon sx={{ fontSize: 18, mr: 0.5, flexShrink: 0 }} />
                              <span>
                                {`${formatarData(campanha.dataInicio, FORMATOS_DATA.PADRAO)} - ${formatarData(campanha.dataFim, FORMATOS_DATA.PADRAO)}`}
                              </span>
                            </Box>
                          </Box>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            minHeight: '24px'
                          }}>
                            <Chip
                              label={campanha.status || 'Ativa'}
                              size="small"
                              color={campanha.status === 'Ativa' ? 'success' : 'default'}
                            />
                          </Box>
                          <Box sx={{ mt: 'auto', pt: 2 }}>
                            <Button
                              variant="contained"
                              fullWidth
                              component={Link}
                              to={`/campanhas/${campanha.id}`}
                              startIcon={<VisibilityIcon />}
                              sx={{ textTransform: 'none', height: '36px' }}
                            >
                              Ver Detalhes
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Slider>
                ) : (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      bgcolor: grey[50],
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma campanha recente disponível.
                    </Typography>
                  </Box>
                )}
              </Box>
            </ModernWidget>
          </Box>}

{/* ================== GERENCIAR PAPÉIS DE USUÁRIOS ================== */}
<Box sx={{ ...styles.fullWidthSection, mt: 4 }}>
  <ModernWidget
    title="Gerenciar Papéis de Usuários"
    icon={SupervisorAccountIcon}
    color={blue[600]}
    action={null}
  >
    {loadingPerfis ? (
      <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    ) : (perfis?.length ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {perfis.map((perfil) => {
          // Monta um objeto de usuário a partir do perfil
          const u = {
            id: perfil.Usuario?.id || perfil.usuario_id,
            nome: perfil.Usuario?.nome || '-',
            username: perfil.Usuario?.username || '-',
            email: perfil.Usuario?.email || '-'
          };

          // Em alguns payloads, pode vir sem Usuario — pulamos estes
          if (!u.id) return null;

          return (
            <Box key={`${perfil.id}-${u.id}`} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ flexGrow: 1 }}>
                {u.nome} ({u.username}) — <strong>{perfil.tipo}</strong>
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleAbrirGerirPapeis(u)}
              >
                Gerir Papéis
              </Button>
            </Box>
          );
        })}
      </Box>
    ) : (
      <Typography variant="body2" color="text.secondary">
        Nenhum perfil/usuário encontrado para esta instituição.
      </Typography>
    ))}
  </ModernWidget>
</Box>

{/* Adicionar após a lista de perfis */}
{paginationInfo && paginationInfo.totalPages > 1 && (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
    <Typography variant="body2" color="text.secondary">
      Página {paginationInfo.currentPage} de {paginationInfo.totalPages} 
      ({paginationInfo.totalItems} usuários no total)
    </Typography>
    
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button 
        size="small" 
        onClick={handlePrevPage} 
        disabled={!paginationInfo.hasPrevPage}
        variant="outlined"
      >
        Anterior
      </Button>
      
      {/* Números das páginas */}
      {Array.from({ length: paginationInfo.totalPages }, (_, i) => i + 1).map(page => (
        <Button
          key={page}
          size="small"
          onClick={() => handlePageChange(page)}
          variant={page === paginationInfo.currentPage ? "contained" : "outlined"}
          sx={{ minWidth: 40 }}
        >
          {page}
        </Button>
      ))}
      
      <Button 
        size="small" 
        onClick={handleNextPage} 
        disabled={!paginationInfo.hasNextPage}
        variant="outlined"
      >
        Próxima
      </Button>
    </Box>
  </Box>
)}

          {/* Seção de Pagamentos — só exibe se módulo PAGAMENTOS activo */}
          {modulosAtivos.includes('PAGAMENTOS') ? (
          dashboardData.planoAtivo ? (
          <Box sx={styles.fullWidthSection}>
            <ModernWidget
              title="Gestão de Pagamentos"
              icon={PaymentIcon}
              color={blue[600]}
              action={
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PaymentIcon />}
                  component={Link}
                  to="/planos-pagamento"
                  sx={{ textTransform: 'none' }}
                >
                  Ver Todos
                </Button>
              }
            >
              {/* Cards de Métricas de Pagamento */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <ModernStatCard
                    title="Total de Pagamentos"
                    value={dashboardData?.estatisticas?.pagamentos?.total || 0}
                    description="Pagamentos recebidos"
                    icon={PaymentIcon}
                    color={green[600]}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <ModernStatCard
                    title="Pagamentos Pendentes"
                    value={dashboardData?.estatisticas?.pagamentos?.pendentes || 0}
                    description="Aguardando confirmação"
                    icon={PaymentIcon}
                    color={orange[600]}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <ModernStatCard
                    title="Taxa de Inadimplência"
                    value={`${(dashboardData?.estatisticas?.taxaInadimplencia || 0).toFixed(1)}%`}
                    description="Média do período"
                    icon={PaymentIcon}
                    color={red[600]}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <ModernStatCard
                    title="Valor Total Recebido"
                    value={formatarMoeda(dashboardData?.estatisticas?.pagamentos?.totalRecebido || 0)}
                    description="Total acumulado"
                    icon={PaymentIcon}
                    color={blue[600]}
                  />
                </Grid>
              </Grid>

              {/* Gráficos */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader title="Status de Pagamentos" />
                    <CardContent>
                      <Box sx={{ height: 300 }}>
                        <PieChart width={400} height={300}>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {chartData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={index === 0 ? green[500] : red[500]}
                              />
                            ))}
                          </Pie>
                          <Legend />
                          <Tooltip />
                        </PieChart>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader title="Evolução de Pagamentos" />
                    <CardContent>
                      <Box sx={{ height: 300 }}>
                        {dashboardData?.estatisticas?.evolucaoPagamentos?.length > 0 ? (
                          <LineChart
                            width={500}
                            height={300}
                            data={dashboardData.estatisticas.evolucaoPagamentos}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="mes" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Line
                              type="monotone"
                              dataKey="valor"
                              stroke={blue[500]}
                              name="Valor Recebido"
                              activeDot={{ r: 8 }}
                            />
                          </LineChart>
                        ) : (
                          <Box
                            sx={{
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Typography color="text.secondary">
                              Nenhum dado disponível
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Tabela de Pagamentos por Estudante */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Pagamentos por Estudante
                  </Typography>
                  {dashboardData?.estatisticas?.estudantes.length > 20 && (
                    <Typography variant="body2" color="text.secondary">
                      Mostrando os 5 últimos de {dashboardData.estatisticas.estudantes.length} estudantes
                    </Typography>
                  )}
                </Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Estudante</TableCell>
                        <TableCell align="right">Total a Pagar</TableCell>
                        <TableCell align="right">Total Pago</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Último Pagamento</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <PagamentosPorEstudante
                        estudantes={dashboardData?.estatisticas?.estudantes.slice(0, 20)}
                        planoAtivo={dashboardData?.planoAtivo}
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
                {dashboardData?.estatisticas?.estudantes.length > 20 && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      component={Link}
                      to={`/planos-pagamento/${dashboardData?.planoAtivo}/estudantes`}
                      startIcon={<PaymentIcon />}
                    >
                      Ver Todos os Estudantes
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Últimos Pagamentos */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Últimos Pagamentos
                  </Typography>
                  {dashboardData?.estatisticas?.pagamentos?.total > 5 && (
                    <Typography variant="body2" color="text.secondary">
                      Mostrando os 5 últimos de {dashboardData.estatisticas.pagamentos.total} pagamentos
                    </Typography>
                  )}
                </Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Estudante</TableCell>
                        <TableCell>Prestação</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell>Método</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(dashboardData?.estatisticas?.pagamentos?.ultimosPagamentos || []).map((pagamento) => (
                        <TableRow key={pagamento.id}>
                          <TableCell>{pagamento.estudante}</TableCell>
                          <TableCell>{pagamento.prestacao}</TableCell>
                          <TableCell>
                            {formatarData(pagamento.data, FORMATOS_DATA.PADRAO)}
                          </TableCell>
                          <TableCell align="right">
                            {formatarMoeda(pagamento.valor)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={pagamento.metodo}
                              color="primary"
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/planos-pagamento/${dashboardData?.planoAtivo}`)}
                              color="primary"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!dashboardData?.estatisticas?.pagamentos?.ultimosPagamentos?.length) && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              Nenhum pagamento registrado
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </ModernWidget>
          </Box>
          ) : (
            <Box sx={{ mb: 4 }}>
              <Paper elevation={0} sx={{ p: 3, border: '1px dashed', borderColor: 'grey.300', borderRadius: 2, textAlign: 'center' }}>
                <PaymentIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  Gestão de Pagamentos
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Crie um plano de pagamento para começar a gerir as propinas dos seus estudantes.
                </Typography>
                <Button variant="contained" component={Link} to="/planos-pagamento/novo" startIcon={<PaymentIcon />}>
                  Criar Plano de Pagamento
                </Button>
              </Paper>
            </Box>
          )) : null}

          {/* 12) SEÇÃO DE LOGS */}
          {canAccessInstitutionLogs() && (
            <Box sx={styles.fullWidthSection}>
              <ModernWidget
                title="Logs do Sistema"
                icon={SecurityIcon}
                color={blue[600]}
                action={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={fetchLogs}
                      disabled={loadingLogs}
                    >
                      Atualizar
                    </Button>
                  </Box>
                }
              >
                <Box sx={{ mb: 3 }}>
                  <InstituicaoLogFilter
                    filters={logFilters}
                    onFilterChange={handleLogFilterChange}
                    onRefresh={fetchLogs}
                    permittedFilters={permittedFilters}
                  />
                </Box>
                <InstituicaoLogsTable
                  logs={logs}
                  loading={loadingLogs}
                  error={logError}
                  onViewDetails={(logId) => {
                    // Ver detalhes do log: logId
                  }}
                  canExport={['ADMIN_INSTITUICAO', 'SUPER_ADMIN', 'AUDITOR_INTERNO'].includes(user?.perfilAdmin)}
                  currentFilters={logFilters}
                  instituicaoId={user?.instituicaoId} // Garantindo que o instituicaoId seja passado
                />
              </ModernWidget>
            </Box>
          )}
          {/* <<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<< */}

{/* Modal para Gerir Papéis */}
{openGerirPapeis && usuarioSelecionado && (
  <Dialog 
    open={openGerirPapeis} 
    onClose={handleFecharGerirPapeis}
    maxWidth="md"
    fullWidth
  >
    <DialogTitle>
      Gerenciar Papéis de {usuarioSelecionado.nome}
    </DialogTitle>
    <DialogContent>
      <GerirPapeisUsuario
        usuario={usuarioSelecionado}
        onRolesUpdated={handleGerirPapeisSaved}
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={handleFecharGerirPapeis}>
        Fechar
      </Button>
    </DialogActions>
  </Dialog>
)}
          {/* Snackbar */}
          <Snackbar
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity="success"
              sx={{ width: '100%' }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </Box>
  );
};

InstituicaoDashboardPage.propTypes = {
  // Se houver props passadas para este componente, defina-as aqui
};

InstituicaoDashboardPage.displayName = 'InstituicaoDashboardPage';

export default InstituicaoDashboardPage;
